import {
    DialogContent as DialogContentComponent,
    DialogOverlay as DialogOverlayComponent,
} from '@reach/dialog';
import styled, {keyframes} from 'styled-components';

import {border, breakpoint, colors} from '@app/styles/theme';

import {IconButton} from '../IconButton';
import {Title} from '../Title';

const fadeInOverlay = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const fadeOutOverlay = keyframes`
    from { opacity: 1; }
    to { opacity: 0; }
`;

const slideUpFadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const slideDownFadeOut = keyframes`
  from { opacity: 1; transform: translateY(0) scale(1); }
  to { opacity: 0; transform: translateY(20px) scale(0.97); }
`;

export const DialogHeader = styled.div`
    margin-bottom: 20px;
    text-align: center;
    padding: 0 24px;
`;

export const DialogOverlay = styled(DialogOverlayComponent)<{mode?: 0 | 1 | 2 | 3; open?: boolean}>`
    position: fixed;
    inset: 0;
    z-index: 1100;
    overflow-y: auto;
    display: grid;
    place-content: center;
    text-align: center;
    background: rgba(0, 0, 0, 0.25);
    animation: ${({open}) => (open === false ? fadeOutOverlay : fadeInOverlay)} 0.25s ease-in-out;

    &.camera-scanner-modal,
    &.pick-scaner-options-modal,
    &.pick-print-format-options-modal,
    &.qr-scanner-modal-invoices {
        z-index: 11000;
        > div {
            &[data-reach-dialog-content] {
                width: 100vw;
                max-width: ${(p) => {
                    switch (p.mode) {
                        case 0:
                            return 'min(100vw - 30px, 440px)';
                        case 1:
                            return 'min(100vw - 30px, 640px)';
                        case 2:
                            return 'min(100vw - 30px, 960px)';
                        case 3:
                            return 'min(100vw - 30px, 1280px)';
                        default:
                            return 'min(100vw - 30px, 800px)';
                    }
                }};
            }
        }
    }

    &.camera-scanner-modal {
        ${DialogHeader} {
            display: none;
        }
        > div {
            &[data-reach-dialog-content] {
                padding: 0;
            }
            @media (max-width: ${breakpoint.phone}) {
                height: 70vh;
            }
        }
    }
`;

export const DialogTitle = styled(Title)`
    font-weight: 700;
`;

export const DialogContent = styled(DialogContentComponent)<{
    mode?: 0 | 1 | 2 | 3;
    open?: boolean;
}>`
    &[data-reach-dialog-content] {
        display: inline-block;
        vertical-align: middle;
        text-align: left;
        position: relative;
        -webkit-overflow-scrolling: touch;
        border-radius: ${border.radius};
        outline: none;
        box-sizing: border-box;
        padding: 20px;
        background-color: ${colors.white};
        white-space: normal;
        animation: ${({open}) => (open === false ? slideDownFadeOut : slideUpFadeIn)} 0.2s ease-out;
        transition: transform 0.25s ease-in-out;

        @media (min-width: ${breakpoint.tablet}) {
            width: 100vw;

            max-width: ${(p) => {
                switch (p.mode) {
                    case 0:
                        return 'min(100vw - 30px, 440px)';
                    case 1:
                        return 'min(100vw - 30px, 640px)';
                    case 2:
                        return 'min(100vw - 30px, 960px)';
                    case 3:
                        return 'min(100vw - 30px, 1280px)';
                    default:
                        return 'min(100vw - 30px, 800px)';
                }
            }};
        }
    }
`;

export const DialogCloseButton = styled(IconButton)`
    position: absolute;
    width: auto;
    height: auto;
    top: 12px;
    right: 12px;
    transition: transform 0.15s ease-in-out;

    &:hover {
        transform: scale(1.15);
    }
`;

export const DialogFooter = styled.div`
    margin-top: 20px;

    @media (min-width: ${breakpoint.tablet}) {
        margin-top: 40px;
    }
`;
